import { supabase, forceSessionRefresh, clearAuthState, prepareAuthFlow, generateCodeVerifier } from './supabase';
import { getUserSubscription, getUserOrders } from './stripe';

// Define types for authentication
export type AuthProvider = 'google' | 'email' | 'phone';
export type AuthResult = {
  success: boolean;
  user: any | null;
  error?: string;
  isNewUser?: boolean;
  redirectTo?: string;
  url?: string;
};

/**
 * Initiates OAuth2 authentication with Google
 * @returns Promise with redirect URL
 */
export const signInWithGoogle = async (): Promise<{ url: string | null; error?: string }> => {
  try {
    // Clear all auth-related items from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('supabase.auth') || 
        key.includes('sb-') || 
        key.includes('pkce-') ||
        key.includes('code-verifier') ||
        key === 'pkce_code_verifier'
      )) {
        console.log(`Removing auth-related key: ${key}`);
        localStorage.removeItem(key);
      }
    }
    
    // Prepare new auth flow
    await prepareAuthFlow();
    
    // Generate and store a code verifier for PKCE
    const codeVerifier = generateCodeVerifier();
    
    // Get the current origin for the redirect URL
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    console.log("Starting Google login with redirect URL:", redirectUrl);
    console.log("Code verifier generated:", codeVerifier.substring(0, 5) + "...");
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          // Pass additional parameters to be saved with the user
          access_type: 'offline',
          prompt: 'consent'
        },
        codeVerifier: codeVerifier
      }
    });
    
    if (error) {
      console.error("Google login error:", error);
      return { url: null, error: error.message };
    }
    
    if (!data.url) {
      return { url: null, error: 'No redirect URL returned from authentication provider' };
    }
    
    return { url: data.url };
  } catch (error: any) {
    console.error("Unexpected Google login error:", error);
    return { url: null, error: error.message };
  }
};

/**
 * Handles the OAuth callback and processes user authentication
 * @param url The full callback URL with auth parameters
 * @returns AuthResult object with authentication status
 */
export const handleAuthCallback = async (url: string): Promise<AuthResult> => {
  try {
    console.log("Processing authentication callback");
    console.log("Current URL for processing:", url);
    
    // Get the code verifier from both possible locations
    const customCodeVerifier = localStorage.getItem('pkce_code_verifier');
    const supabaseCodeVerifier = localStorage.getItem('supabase.auth.codeVerifier'); 
    
    if (!customCodeVerifier && !supabaseCodeVerifier) {
      console.error("No code verifier found in localStorage");
      return {
        success: false,
        user: null,
        error: "Authentication failed: Missing code verifier. Please try again."
      };
    }
    
    // Use the first available code verifier
    const codeVerifier = customCodeVerifier || supabaseCodeVerifier;
    console.log(`Using code verifier: ${codeVerifier.substring(0, 5)}...`);
    
    // Get the full URL including hash fragments
    const fullUrl = url;
    console.log("Current URL for processing:", fullUrl);
    
    // Parse the URL to extract code or token
    const parsedUrl = new URL(fullUrl);
    let code = parsedUrl.searchParams.get('code');
    let token = parsedUrl.searchParams.get('token');
    let type = parsedUrl.searchParams.get('type');
    let error = parsedUrl.searchParams.get('error');
    let errorDescription = parsedUrl.searchParams.get('error_description');
    let state = parsedUrl.searchParams.get('state');
    
    // If no code/token in URL params, check hash fragment
    if ((!code && !token && !state) && parsedUrl.hash) {
      const hashParams = new URLSearchParams(parsedUrl.hash.substring(1));
      code = code || hashParams.get('code');
      token = token || hashParams.get('token');
      type = type || hashParams.get('type');
      error = error || hashParams.get('error');
      errorDescription = errorDescription || hashParams.get('error_description');
      state = state || hashParams.get('state');
    }
    
    console.log("Extracted auth parameters:", { 
      code: !!code, 
      token: !!token, 
      type, 
      error, 
      state: !!state 
    });
    
    // Handle error from auth provider
    if (error) {
      console.error(`Authentication error from provider: ${error}`, errorDescription);
      return { 
        success: false, 
        user: null, 
        error: errorDescription || error 
      };
    }
    
    if (!code && !token && !state) {
      console.error("No authentication code, token, or state found");
      return { 
        success: false, 
        user: null, 
        error: "No authentication parameters found" 
      };
    }
    
    // Process based on auth type
    if (type === 'recovery') {
      // Password reset flow
      return { 
        success: true, 
        user: null, 
        redirectTo: '/update-password' 
      };
    } else {
      // Normal authentication flow (signup, signin, etc.)
      let authResult;
      
      // Handle token (email confirmation)
      if (token) {
        console.log("Processing token-based authentication");
        authResult = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type === 'signup' ? 'signup' : 'email',
        });
      } 
      // Handle code (OAuth)
      else if (code) {
        console.log("Processing OAuth code exchange");
        console.log(`Code: ${code.substring(0, 5)}... (length: ${code.length})`);
        console.log(`Code verifier: ${codeVerifier.substring(0, 5)}... (length: ${codeVerifier.length})`);
        
        try {
          // Exchange the code for a session
          authResult = await supabase.auth.exchangeCodeForSession(code);
          
          // Clear the code verifier after successful exchange
          localStorage.removeItem('pkce_code_verifier');
          localStorage.removeItem('supabase.auth.codeVerifier');
          localStorage.removeItem('supabase.auth.flowState');
          localStorage.removeItem('supabase.auth.pkceState');
        } catch (codeError) {
          console.error("Error exchanging code for session:", codeError);
          // Clear the code verifier on error
          localStorage.removeItem('pkce_code_verifier');
          localStorage.removeItem('supabase.auth.codeVerifier');
          localStorage.removeItem('supabase.auth.flowState');
          localStorage.removeItem('supabase.auth.pkceState');
          throw codeError;
        }
      } 
      // Handle state parameter (sometimes used in OAuth flows)
      else if (state) {
        console.log("Processing OAuth with state parameter");
        // For state parameter, we need to get the session directly
        authResult = await supabase.auth.getSession();
      } else {
        return { 
          success: false, 
          user: null, 
          error: "Invalid authentication parameters" 
        };
      }
      
      if (authResult.error) {
        console.error('Error processing authentication:', authResult.error);
        // Clear the code verifier on error
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('supabase.auth.codeVerifier');
        localStorage.removeItem('supabase.auth.flowState');
        localStorage.removeItem('supabase.auth.pkceState');
        return { 
          success: false, 
          user: null, 
          error: authResult.error.message 
        };
      }
      
      if (!authResult.data.session) {
        console.error('No session created during authentication');
        // Clear the code verifier on error
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('supabase.auth.codeVerifier');
        localStorage.removeItem('supabase.auth.flowState');
        localStorage.removeItem('supabase.auth.pkceState');
        return { 
          success: false, 
          user: null, 
          error: "Failed to create session" 
        };
      }
      
      const user = authResult.data.user;
      
      if (!user) {
        console.error('No user data in authentication result');
        // Clear the code verifier on error
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('supabase.auth.codeVerifier');
        localStorage.removeItem('supabase.auth.flowState');
        localStorage.removeItem('supabase.auth.pkceState');
        return {
          success: false,
          user: null,
          error: "No user data found"
        };
      }
      
      // Determine if this is a new user
      const isNewUser = user.app_metadata.provider === 'google' && 
                        user.app_metadata.providers?.length === 1 &&
                        user.created_at === user.updated_at;
      
      // Force a session refresh to ensure we have the latest data
      await forceSessionRefresh();
      
      // Check if user has premium status
      let isPremium = false;
      try {
        const subscription = await getUserSubscription();
        const orders = await getUserOrders();
        
        // Check if user has active subscription or lifetime purchase
        isPremium = subscription?.status === 'active' || 
                   orders?.some(order => order.payment_status === 'paid');
      } catch (error) {
        console.warn("Error checking premium status:", error);
        // Continue with non-premium status
      }
      
      // Log the successful authentication
      logAuthEvent({
        userId: user.id,
        email: user.email,
        action: isNewUser ? 'signup' : 'login',
        success: true,
        provider: user.app_metadata.provider,
        isPremium
      });
      
      // Check if user has a complete profile
      let hasCompleteProfile = false;
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, location, bio')
          .eq('id', user.id)
          .single();
          
        if (!profileError && profile) {
          hasCompleteProfile = !!profile.full_name && !!profile.location;
        }
      } catch (err) {
        console.warn("Error checking profile completeness:", err);
        // Continue with incomplete profile assumption
      }
      
      return {
        success: true,
        user,
        isNewUser,
        redirectTo: !hasCompleteProfile ? '/profile-completion' : '/'
      };
    }
  } catch (err) {
    console.error("Error handling auth callback:", err);
    
    // Clear the code verifier on error
    localStorage.removeItem('pkce_code_verifier');
    localStorage.removeItem('supabase.auth.codeVerifier');
    localStorage.removeItem('supabase.auth.flowState');
    localStorage.removeItem('supabase.auth.pkceState');
    
    // Log the failed authentication attempt
    logAuthEvent({
      userId: null,
      email: null,
      action: 'callback_error',
      success: false,
      details: err instanceof Error ? err.message : String(err)
    });
    
    return {
      success: false,
      user: null,
      error: err instanceof Error ? err.message : 'An unknown error occurred'
    };
  }
};

/**
 * Logs authentication events for security monitoring
 * @param event The authentication event to log
 */
const logAuthEvent = async (event: {
  userId: string | null;
  email: string | null;
  action: string;
  success: boolean;
  provider?: string;
  details?: string;
  isPremium?: boolean;
}): Promise<void> => {
  try {
    // Log to console for development
    console.log('Auth event:', event);
    
    // In production, you would send this to your logging service
    // For now, we'll just log to Supabase if we have a valid session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (session) {
      // Log to a hypothetical auth_logs table (you would need to create this)
      try {
        const { error } = await supabase
          .from('auth_logs')
          .insert({
            user_id: event.userId,
            email: event.email,
            action: event.action,
            success: event.success,
            provider: event.provider,
            details: event.details,
            ip_address: null, // Would need to be captured server-side
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
          });
          
        if (error && error.code !== '42P01') { // Ignore table doesn't exist error
          console.warn('Error logging auth event:', error);
        }
      } catch (logError) {
        console.warn('Error logging to auth_logs table:', logError);
      }
    }
  } catch (error) {
    console.error('Error logging auth event:', error);
    // Non-critical error, don't throw
  }
};